import React, { useState, useEffect, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { RadioButton } from 'primereact/radiobutton';
import { classNames } from 'primereact/utils';
import OtherCostDropdown from '../../../../../../../components/common/OtherCostDropdown';
import { useLanguage } from '../../../../../../../contexts/LanguageContext';
import { usePermissions } from '../../../../../../../hooks/usePermissions';
import { useGetDealerPaymentCurrencyQuery, useGetDealerAgreementTypeQuery, useGetWarrantyClaimsOtherCostByIdQuery } from '../../../../../../../api';
import WarrantyPaymentTypeRadio from '../../../../../../../components/common/WarrantyPaymentTypeRadio';
import { InputTextarea } from 'primereact/inputtextarea';
import { WarrantyClaimStatus } from '../../../../../../../utils/warrantyClaimsEnums';
import { Roles } from '../../../../../../../utils/roleEnums';

const OtherCostFormDialog = ({ disabledStatuses, claimStatus, claimId, warrType, isDomestic, dialogVisible, hideDialog, onSave, onDelete, editingOtherCost, authorizationCheck, onUpdateSuccess }) => {
    const { th, getCurrentLanguageId, getCurrentLocale } = useLanguage();
    const { hasWarrantyTeamRole, hasDealerRole, hasAnyRole } = usePermissions();

    // API'den dealer'ın para birimini çek
    const { data: currencyData, isLoading: isDealerCurrencyLoading } = useGetDealerPaymentCurrencyQuery();

    // Get warranty claims other cost by ID when editing
    const { data: warrantyClaimsOtherCostById, isLoading: isLoadingOtherCost, refetch: refetchOtherCostById } = useGetWarrantyClaimsOtherCostByIdQuery({ id: editingOtherCost?.id, languageId: getCurrentLanguageId() }, { skip: !editingOtherCost?.id });

    const [formData, setFormData] = useState({
        id: null,
        claimId: claimId,
        selectType: 0,
        description: '',
        userPrice: 0,
        price: 0,
        currency: currencyData?.defaultCurrency || 'TRY',
        warrantyType: null,
        hour: null, // selectType === 2 için
        kmHour: null, // roadsideType için (KM veya Saat değeri)
        roadsideType: null, // 1: KM, 2: Saat
        partQuantity: null // selectType === 1 (Parça) için adet miktarı
    });

    // API'den dealer agreement type'ı çek (sadece selectType = 3 ve yurt dışı olduğunda)
    const { data: agreementData, isLoading: isAgreementLoading } = useGetDealerAgreementTypeQuery(
        { claimId },
        {
            skip: formData.selectType !== 3 || isDomestic === true
        }
    );

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const canUpdateInReturnedToKarsan =
        editingOtherCost &&
        claimStatus === WarrantyClaimStatus.RETURNED_TO_KARSAN &&
        hasAnyRole([Roles.WARRANTY_TEAM, Roles.WARRANTY_MANAGER, Roles.ADMIN]);

    // selectType === 3 ve warrantyType === 2 kontrolü için değişken
    const isUnitTypeRequired = formData.selectType === 3 && formData.warrantyType === 2;

    // Real-time TotalAmount hesaplama
    // Item (1) ve Labor (2) için: PartQuantity × Price (Labor'da hour, partQuantity rolü oynar)
    // RoadsideAssistance (3) / VehicleTowing (4) için: Price
    const computedTotalAmount = useMemo(() => {
        const price = Number(formData.price) || 0;
        if (formData.selectType === 1) {
            return (Number(formData.partQuantity) || 0) * price;
        }
        if (formData.selectType === 2) {
            return (Number(formData.hour) || 0) * price;
        }
        return price;
    }, [formData.selectType, formData.partQuantity, formData.hour, formData.price]);

    // API'den gelen para birimi seçeneklerini kullan
    const currencyOptions = currencyData?.currencyOptions || [
        { label: 'TRY', value: 'TRY' },
        { label: 'USD', value: 'USD' },
        { label: 'EUR', value: 'EUR' }
    ];

    // Reset form to initial state
    const resetForm = () => {
        setFormData({
            id: null,
            claimId: claimId,
            selectType: 0,
            description: '',
            price: 0,
            currency: currencyData?.defaultCurrency || 'TRY',
            warrantyType: null,
            hour: null,
            kmHour: null,
            roadsideType: null,
            partQuantity: null
        });
        setErrors({});
        setIsSubmitting(false);
    };

    // Handle dialog close
    const handleDialogClose = () => {
        resetForm();
        hideDialog();
    };

    useEffect(() => {
        if (dialogVisible) {
            if (editingOtherCost) {
                // Use getById data if available, otherwise fall back to editingOtherCost data
                const otherCostData = warrantyClaimsOtherCostById?.data || editingOtherCost;
                const id = otherCostData.id || otherCostData.Id || otherCostData.otherCostId || otherCostData.otherCostID;

                setFormData({
                    ...otherCostData,
                    id: id,
                    claimId: claimId,
                    acceptedAmount: otherCostData.acceptedAmount || 0,
                    selectType: otherCostData.selectType || 0,
                    description: otherCostData.description || '',
                    price: otherCostData.price || 0,
                    currency: otherCostData.currency || currencyData?.defaultCurrency || 'TRY',
                    warrantyType: otherCostData.warrantyType ? Number(otherCostData.warrantyType) : null,
                    hour: otherCostData.hour || null,
                    kmHour: otherCostData.kmHour || null,
                    roadsideType: otherCostData.roadsideType || null,
                    partQuantity: otherCostData.partQuantity || null
                });
            } else {
                resetForm();
            }
        }
    }, [dialogVisible, editingOtherCost, claimId, warrantyClaimsOtherCostById]);

    // Reset form when dialog becomes invisible
    useEffect(() => {
        if (!dialogVisible) {
            resetForm();
        }
    }, [dialogVisible]);

    // isUnitTypeRequired koşulu false olduğunda ilgili alanları temizle
    useEffect(() => {
        if (!isUnitTypeRequired) {
            setFormData((prev) => ({
                ...prev,
                roadsideType: null,
                kmHour: null
            }));
        }
    }, [isUnitTypeRequired]);

    // selectType === 2 koşulu false olduğunda hour'u temizle
    useEffect(() => {
        if (formData.selectType !== 2) {
            setFormData((prev) => ({
                ...prev,
                hour: null
            }));
        }
    }, [formData.selectType]);

    // selectType === 1 koşulu false olduğunda partQuantity'i temizle
    useEffect(() => {
        if (formData.selectType !== 1) {
            setFormData((prev) => ({
                ...prev,
                partQuantity: null
            }));
        }
    }, [formData.selectType]);

    // Agreement type'a göre label belirle
    const getAgreementLabel = () => {
        if (agreementData?.agreementType === 1) {
            return th('warrantyClaims.detail.otherCosts.km');
        } else if (agreementData?.agreementType === 2) {
            return th('warrantyClaims.detail.otherCosts.hour');
        }
        return th('warrantyClaims.detail.otherCosts.price');
    };

    const validateForm = () => {
        const newErrors = {};
        const isUnitTypeRequired = formData.selectType === 3 && formData.warrantyType === 2;

        if (!formData.selectType || formData.selectType === 0) {
            newErrors.selectType = 'Maliyet türü seçilmelidir';
        }

        if (!formData.warrantyType) {
            newErrors.warrantyType = th('warrantyClaims.detail.labor.required');
        }

        if (!formData.description?.trim()) {
            newErrors.description = 'Açıklama girilmelidir';
        }

        // isUnitTypeRequired değilse price zorunlu
        if (!isUnitTypeRequired && (!formData.price || formData.price <= 0)) {
            newErrors.price = "Fiyat 0'dan büyük olmalıdır";
        }

        if (formData.selectType === 2 && (!formData.hour || formData.hour <= 0)) {
            newErrors.hour = th('warrantyClaims.detail.otherCosts.hourRequired');
        }

        if (formData.selectType === 1 && (!formData.partQuantity || formData.partQuantity <= 0)) {
            newErrors.partQuantity = th('warrantyClaims.detail.otherCosts.partQuantityRequired');
        }

        // selectType === 3 ve warrantyType === 2 olduğunda roadsideType zorunlu
        if (isUnitTypeRequired && !formData.roadsideType) {
            newErrors.roadsideType = 'Birim türü seçilmelidir';
        }

        // roadsideType seçildiğinde kmHour kontrolü
        if (isUnitTypeRequired && formData.roadsideType && (!formData.kmHour || formData.kmHour <= 0)) {
            newErrors.kmHour = th('warrantyClaims.detail.otherCosts.kmHourRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        try {
            const isUnitTypeRequired = formData.selectType === 3 && formData.warrantyType === 2;

            // Temel payload
            const payload = {
                id: formData.id,
                claimId: formData.claimId,
                selectType: formData.selectType,
                description: formData.description,
                currency: formData.currency,
                warrantyType: Number(formData.warrantyType)
            };

            // Price ekle (isUnitTypeRequired && ekleme modunda eklenmesin)
            if (!(isUnitTypeRequired && !editingOtherCost)) {
                payload.price = formData.price;
            }

            // acceptedAmount ekle (edit modunda); dealer ise null gönder
            if (hasDealerRole()) {
                payload.acceptedAmount = null;
            } else if (formData.acceptedAmount !== undefined && formData.acceptedAmount !== null) {
                payload.acceptedAmount = formData.acceptedAmount;
            }

            // userPrice varsa ekle (yurt dışı durumu için)
            if (formData.userPrice !== undefined && formData.userPrice !== null) {
                payload.userPrice = formData.userPrice;
            }

            // selectType === 1 ise partQuantity ekle
            if (formData.selectType === 1 && formData.partQuantity) {
                payload.partQuantity = formData.partQuantity;
            }

            // selectType === 2 (Labor) ise sadece hour gönder; partQuantity payload'a EKLENMEZ
            if (formData.selectType === 2 && formData.hour) {
                payload.hour = formData.hour;
            }

            // selectType === 3 && warrantyType === 2 ise roadsideType ve kmHour ekle
            if (isUnitTypeRequired) {
                if (formData.roadsideType) {
                    payload.roadsideType = formData.roadsideType;
                }
                if (formData.kmHour !== null && formData.kmHour !== undefined) {
                    payload.kmHour = formData.kmHour;
                }
            }

            const success = await onSave(payload);

            // Eğer update işlemi başarılıysa ve editingOtherCost varsa, getById endpoint'ini çağır
            if (success && editingOtherCost && onUpdateSuccess) {
                await refetchOtherCostById();
                onUpdateSuccess();
            }

            if (success) {
                resetForm();
                hideDialog();
            }
        } catch (error) {
            console.error('Kaydetme hatası:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!editingOtherCost?.id) return;

        setIsSubmitting(true);
        try {
            const result = await onDelete(editingOtherCost.id);
            if (result) {
                resetForm();
                hideDialog();
            }
        } catch (error) {
            console.error('Silme hatası:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const footer = (
        <div className="flex justify-content-between items-center">
            {editingOtherCost && !hasWarrantyTeamRole() && <Button label={th('warrantyClaims.detail.otherCosts.delete')} icon="pi pi-trash" className="p-button-danger" onClick={handleDelete} disabled={disabledStatuses} />}
            <div className="flex gap-2 justify-content-end w-full">
                <Button label={th('warrantyClaims.detail.otherCosts.cancel')} icon="pi pi-times" onClick={handleDialogClose} outlined disabled={isSubmitting} />
                <Button
                    label={editingOtherCost ? th('warrantyClaims.detail.otherCosts.update') : th('warrantyClaims.detail.otherCosts.create')}
                    icon="pi pi-check"
                    onClick={handleSave}
                    disabled={isSubmitting || (disabledStatuses && !canUpdateInReturnedToKarsan)}
                />
            </div>
        </div>
    );

    return (
        <Dialog
            visible={dialogVisible && !isLoadingOtherCost}
            style={{ width: '50rem' }}
            header={editingOtherCost ? th('warrantyClaims.detail.otherCosts.editTitle') : th('warrantyClaims.detail.otherCosts.addTitle')}
            modal
            className="p-fluid"
            footer={footer}
            onHide={handleDialogClose}
        >
            <div className="field mb-2">
                <label htmlFor="selectType">{th('warrantyClaims.detail.otherCosts.selectType')} *</label>
                <OtherCostDropdown
                    value={formData.selectType}
                    onChange={(value) => setFormData((prev) => ({ ...prev, selectType: value }))}
                    className={classNames({ 'p-invalid': errors.selectType })}
                    disabled={isSubmitting || disabledStatuses || hasWarrantyTeamRole()}
                />
                {errors.selectType && <small className="p-error">{errors.selectType}</small>}
            </div>

            {formData.selectType === 1 && (
                <div className="field mb-2">
                    <label htmlFor="partQuantity">{th('warrantyClaims.detail.otherCosts.partQuantity')} *</label>
                    <InputNumber
                        id="partQuantity"
                        value={formData.partQuantity}
                        onChange={(e) => setFormData((prev) => ({ ...prev, partQuantity: e.value }))}
                        mode="decimal"
                        locale={getCurrentLocale()}
                        minFractionDigits={0}
                        maxFractionDigits={0}
                        min={1}
                        step={1}
                        placeholder={th('warrantyClaims.detail.otherCosts.partQuantityPlaceholder')}
                        disabled={isSubmitting || disabledStatuses || hasWarrantyTeamRole()}
                        className={classNames({ 'p-invalid': errors.partQuantity })}
                    />
                    {errors.partQuantity && <small className="p-error">{errors.partQuantity}</small>}
                </div>
            )}

            <div className="field mb-2">
                <label className="mb-1">{th('warrantyClaims.detail.partDialog.warrantyType')} *</label>
                <WarrantyPaymentTypeRadio
                    value={formData.warrantyType}
                    onChange={(value) => setFormData((prev) => ({ ...prev, warrantyType: value }))}
                    className={classNames({ 'p-invalid': errors.warrantyType })}
                    disabled={isSubmitting || disabledStatuses || hasWarrantyTeamRole()}
                    excludeOptions={warrType === 'MSG' ? ['2'] : []}
                    warrType={warrType}
                />
                {errors.warrantyType && <small className="p-error">{errors.warrantyType}</small>}
            </div>

            {/* selectType === 3 ve warrantyType === 2 olduğunda KM/Saat seçimi */}
            {isUnitTypeRequired && (
                <div className="field mb-2">
                    <label className="mb-2">{th('warrantyClaims.detail.otherCosts.unitType')} *</label>
                    <div className="flex flex-wrap gap-3">
                        <div className="flex align-items-center">
                            <RadioButton
                                inputId="roadsideType_km"
                                name="roadsideType"
                                value={1}
                                onChange={(e) => setFormData((prev) => ({ ...prev, roadsideType: e.value }))}
                                checked={formData.roadsideType === 1}
                                disabled={isSubmitting || disabledStatuses || hasWarrantyTeamRole()}
                            />
                            <label htmlFor="roadsideType_km" className="ml-2">
                                {th('warrantyClaims.detail.otherCosts.km')}
                            </label>
                        </div>
                        <div className="flex align-items-center">
                            <RadioButton
                                inputId="roadsideType_hour"
                                name="roadsideType"
                                value={2}
                                onChange={(e) => setFormData((prev) => ({ ...prev, roadsideType: e.value }))}
                                checked={formData.roadsideType === 2}
                                disabled={isSubmitting || disabledStatuses || hasWarrantyTeamRole()}
                            />
                            <label htmlFor="roadsideType_hour" className="ml-2">
                                {th('warrantyClaims.detail.otherCosts.hour')}
                            </label>
                        </div>
                    </div>
                    {errors.roadsideType && <small className="p-error">{errors.roadsideType}</small>}
                </div>
            )}

            {/* roadsideType seçildiğinde kmHour input'u (KM veya Saat değeri) */}
            {isUnitTypeRequired && formData.roadsideType && (
                <div className="field mb-2">
                    <label htmlFor="kmHour">{formData.roadsideType === 1 ? th('warrantyClaims.detail.otherCosts.km') : th('warrantyClaims.detail.otherCosts.hour')} *</label>
                    <InputNumber
                        id="kmHour"
                        value={formData.kmHour}
                        onValueChange={(e) => setFormData((prev) => ({ ...prev, kmHour: e.value }))}
                        mode="decimal"
                        minFractionDigits={0}
                        maxFractionDigits={formData.roadsideType === 2 ? 1 : 0}
                        min={0}
                        step={formData.roadsideType === 2 ? 0.1 : 1}
                        placeholder={formData.roadsideType === 1 ? th('warrantyClaims.detail.otherCosts.kmPlaceholder') : th('warrantyClaims.detail.otherCosts.hourPlaceholder')}
                        disabled={isSubmitting || disabledStatuses || hasWarrantyTeamRole()}
                        className={classNames({ 'p-invalid': errors.kmHour })}
                    />
                    {errors.kmHour && <small className="p-error">{errors.kmHour}</small>}
                </div>
            )}

            {formData.selectType === 2 && (
                <div className="field mb-2">
                    <label htmlFor="hour">{th('warrantyClaims.detail.otherCosts.hour')} *</label>
                    <InputNumber
                        id="hour"
                        value={formData.hour}
                        onChange={(e) => setFormData((prev) => ({ ...prev, hour: e.value }))}
                        mode="decimal"
                        locale={getCurrentLocale()}
                        minFractionDigits={0}
                        maxFractionDigits={2}
                        min={0}
                        placeholder={th('warrantyClaims.detail.otherCosts.hourPlaceholder')}
                        disabled={isSubmitting || disabledStatuses || hasWarrantyTeamRole()}
                        className={classNames({ 'p-invalid': errors.hour })}
                    />
                    {errors.hour && <small className="p-error">{errors.hour}</small>}
                </div>
            )}

            <div className="field mb-2">
                <label htmlFor="description">{th('warrantyClaims.detail.otherCosts.description')}</label>
                <InputTextarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder={th('warrantyClaims.detail.otherCosts.descriptionPlaceholder')}
                    disabled={isSubmitting || disabledStatuses || hasWarrantyTeamRole()}
                    className={classNames({ 'p-invalid': errors.description })}
                    rows={3}
                    style={{ overflowY: 'auto' }}
                />
                {errors.description && <small className="p-error">{errors.description}</small>}
            </div>
            {/* Normal price input - isUnitTypeRequired && ekleme modunda gizle */}
            {(formData.selectType !== 3 || isDomestic === true) && !(isUnitTypeRequired && !editingOtherCost) && (
                <div className="field mb-2">
                    <label htmlFor="price">
                        {formData.selectType === 2
                            ? th('warrantyClaims.detail.otherCosts.hourlyRate')
                            : th('warrantyClaims.detail.otherCosts.price')}{' '}
                        *
                    </label>
                    <InputNumber
                        id="price"
                        value={formData.price}
                        onChange={(e) => {
                            const numericValue = e.value ?? 0;
                            setFormData((prev) => ({
                                ...prev,
                                price: numericValue
                            }));
                        }}
                        mode="decimal"
                        locale={getCurrentLocale()}
                        minFractionDigits={0}
                        maxFractionDigits={2}
                        step={0.01}
                        placeholder={
                            formData.selectType === 2
                                ? th('warrantyClaims.detail.otherCosts.hourlyRatePlaceholder')
                                : th('warrantyClaims.detail.otherCosts.pricePlaceholder')
                        }
                        disabled={isSubmitting || disabledStatuses || (isUnitTypeRequired && editingOtherCost) || hasWarrantyTeamRole()}
                        className={classNames({ 'p-invalid': errors.price })}
                    />
                    {errors.price && <small className="p-error">{errors.price}</small>}
                </div>
            )}

            {/* Agreement Type Input - sadece selectType = 3 ve yurt dışı olduğunda göster */}
            {/* isUnitTypeRequired && ekleme modunda gizle */}
            {formData.selectType === 3 && isDomestic !== true && agreementData && !(isUnitTypeRequired && !editingOtherCost) && (
                <>
                    <div className="field mb-2">
                        <label htmlFor="userPrice">{th('warrantyClaims.detail.otherCosts.price')} *</label>
                        <InputNumber
                            id="price"
                            value={formData.userPrice ?? formData.price}
                            onChange={(e) => {
                                const numericValue = e.value ?? 0;
                                setFormData((prev) => {
                                    const newFormData = {
                                        ...prev,
                                        userPrice: numericValue
                                    };

                                    // Anlık hesaplama
                                    if (agreementData && agreementData.price && prev.selectType === 3) {
                                        newFormData.price = agreementData.price * numericValue;
                                    }

                                    return newFormData;
                                });
                            }}
                            mode="decimal"
                            locale="tr-TR"
                            minFractionDigits={0}
                            maxFractionDigits={2}
                            step={0.01}
                            placeholder={th('warrantyClaims.detail.otherCosts.pricePlaceholder')}
                            disabled={isSubmitting || disabledStatuses || (isUnitTypeRequired && editingOtherCost) || hasWarrantyTeamRole()}
                            className={classNames({ 'p-invalid': errors.price })}
                        />
                        {errors.price && <small className="p-error">{errors.price}</small>}
                    </div>
                    <div className="field mb-2">
                        <label htmlFor="agreementType">{getAgreementLabel()}</label>
                        <InputNumber id="agreementType" value={agreementData.price} mode="decimal" locale="tr-TR" minFractionDigits={0} maxFractionDigits={2} disabled={true} className="p-disabled" />
                    </div>
                    <div className="field mb-2">
                        <label htmlFor="agreementType">{th('warrantyClaims.detail.otherCosts.calculatedAmount')}</label>
                        <InputNumber id="agreementType" value={formData.price} mode="decimal" locale="tr-TR" minFractionDigits={0} maxFractionDigits={2} disabled={true} className="p-disabled" />
                    </div>
                </>
            )}

            {/* Anlık hesaplanan Toplam Tutar (readonly) */}
            {!(isUnitTypeRequired && !editingOtherCost) && (
                <div className="field mb-2">
                    <label htmlFor="totalAmountReadonly">{th('warrantyClaims.detail.partTable.totalAmount')}</label>
                    <InputNumber
                        id="totalAmountReadonly"
                        value={computedTotalAmount}
                        mode="decimal"
                        locale={getCurrentLocale()}
                        minFractionDigits={2}
                        maxFractionDigits={2}
                        disabled
                        className="p-disabled"
                    />
                </div>
            )}

            {editingOtherCost && (
                <div className="field mb-2">
                    {/* Eğer yetki varsa disabled kaldırılacak. Garanti ekibi her zaman düzenleyebilir. */}
                    <label>{th('warrantyClaims.detail.partTable.acceptedAmount')}</label>
                    <InputNumber
                        value={formData.acceptedAmount || 0}
                        onChange={(e) => {
                            const numericValue = e.value ?? 0;
                            setFormData((prev) => ({
                                ...prev,
                                acceptedAmount: numericValue
                            }));
                        }}
                        mode="decimal"
                        locale={getCurrentLocale()}
                        minFractionDigits={0}
                        maxFractionDigits={2}
                        step={0.01}
                        disabled={!authorizationCheck && !hasWarrantyTeamRole()}
                    />
                </div>
            )}
            <div className="field mb-2">
                <label htmlFor="currency">{th('warrantyClaims.detail.otherCosts.currency')}</label>
                <Dropdown
                    id="currency"
                    value={formData.currency}
                    options={currencyOptions}
                    onChange={(e) => setFormData((prev) => ({ ...prev, currency: e.value }))}
                    placeholder={th('warrantyClaims.detail.otherCosts.currencyPlaceholder')}
                    disabled={isSubmitting || isDealerCurrencyLoading || disabledStatuses || hasWarrantyTeamRole()}
                />
            </div>
        </Dialog>
    );
};

export default OtherCostFormDialog;
